import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import {
  formatIqd,
  fromIqd,
  fromMillidinars,
  resolveRefillBonus,
  sum,
  type CustomerAccountDto,
  type Millidinars,
  type RefillReceiptDto,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { api, ApiError } from '@/lib/api';
import {
  CASH_TENDER,
  TenderPicker,
  tenderBody,
  useElectronicTenders,
  type Tender,
} from '@/features/tabs/TenderPicker';
import { useRefillOptions } from './useCustomers';

interface RefillDialogProps {
  customer: CustomerAccountDto;
  currencySymbol: string;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Take a deposit — SPEC §5.5 "Refill".
 *
 * The bonus is previewed with `resolveRefillBonus`, the SAME pure function the
 * server uses, seeded with the venue's own tiers and rounding rule. A cashier
 * tells the customer what they will receive before taking the notes, so a
 * client-side approximation that differed from the credited amount would be a
 * promise the venue then breaks.
 */
export function RefillDialog({ customer, currencySymbol, onClose, onDone }: RefillDialogProps) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { data: options } = useRefillOptions(true);
  /*
   * A deposit writes a PAID refill bill, so the route wants `bills.create`
   * on top of `customers.refill` (2026-09-24). The Refill button still shows
   * for a refill holder; without the second key this dialog names the closed
   * gate rather than offering FIB — "take it in the FIB app first", then a
   * refusal, would leave the money in FIB and the balance never credited.
   */
  const canTakePayment = can('bills.create');

  const [amountIqd, setAmountIqd] = useState('');
  const [note, setNote] = useState('');
  /** Notes in the drawer, or a switched-on provider (2026-09-24). Never the balance. */
  const [tender, setTender] = useState<Tender>(CASH_TENDER);
  const { providers, failed: providersFailed } = useElectronicTenders();
  const [receipt, setReceipt] = useState<RefillReceiptDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const money = (value: Millidinars) => formatIqd(value, { currencySymbol });
  const amount = fromIqd(Math.max(0, Math.floor(Number(amountIqd) || 0)));

  const preview = useMemo(() => {
    if (!options) return null;
    return resolveRefillBonus(amount, {
      flatPercent: options.flatPercent,
      tiers: options.tiers,
      rounding: options.roundingRule,
    });
  }, [amount, options]);

  const credited = preview ? sum([amount, preview.bonusMd]) : amount;
  const newBalance = sum([fromMillidinars(customer.balanceMd), credited]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canTakePayment) return;
    setError(null);
    setSubmitting(true);

    try {
      setReceipt(
        await api.post<RefillReceiptDto>(`/customers/${customer.id}/refill`, {
          amountIqd: Math.floor(Number(amountIqd) || 0),
          ...(note.trim() ? { note: note.trim() } : {}),
          // Nothing for cash — absent is cash, and the body stays what it was.
          ...(tender.method === 'ELECTRONIC' ? tenderBody(tender) : {}),
        }),
      );
      onDone();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('customers.refill.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';

  if (receipt) {
    return (
      <Shell label={t('customers.refill.done')} onClose={onClose}>
        <RefillDone receipt={receipt} currencySymbol={currencySymbol} onClose={onClose} />
      </Shell>
    );
  }

  return (
    <Shell label={t('customers.refill.title')} onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(event)}>
        <h2 className="text-lg font-semibold text-content">
          {t('customers.refill.title')} — {customer.fullName}
        </h2>

        <label className="mt-4 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('customers.refill.amount')}
          </span>
          <input
            type="number"
            min="1"
            step="1"
            required
            autoFocus
            value={amountIqd}
            onChange={(event) => setAmountIqd(event.target.value)}
            className={`${inputClass} tabular`}
          />
        </label>

        <div className="mt-3">
          <TenderPicker
            value={tender}
            onChange={setTender}
            providers={canTakePayment ? providers : []}
            providersFailed={providersFailed}
            allowAccountBalance={false}
          />
        </div>

        <label className="mt-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('customers.refill.note')}
          </span>
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={300}
            className={inputClass}
          />
        </label>

        <dl className="tabular mt-4 rounded-card bg-raised p-3 text-sm">
          <Row
            label={t('customers.refill.currentBalance')}
            value={money(fromMillidinars(customer.balanceMd))}
          />
          <Row label={t('customers.refill.bonus')} value={money(preview?.bonusMd ?? fromIqd(0))} />
          <Row label={t('customers.refill.credited')} value={money(credited)} strong />
          <Row label={t('customers.refill.newBalance')} value={money(newBalance)} />
        </dl>

        {options && preview ? (
          <p className="mt-2 text-xs text-content-muted">
            {preview.bonusPercentApplied === 0
              ? t('customers.refill.noBonus')
              : preview.matchedTierMinAmountMd
                ? t('customers.refill.tierApplied', {
                    amount: money(fromMillidinars(preview.matchedTierMinAmountMd)),
                    percent: preview.bonusPercentApplied,
                  })
                : t('customers.refill.flatApplied', { percent: preview.bonusPercentApplied })}
          </p>
        ) : null}

        {/* The deposit moves the employee's box (§12.1) — as cash, or as an
            electronic payment the drawer never holds. Saying so here means
            the box screen never surprises anyone. */}
        <p className="mt-2 text-xs text-content-secondary">
          {tender.method === 'ELECTRONIC' && tender.providerLabel
            ? t('tender.refillBoxNotice', { provider: tender.providerLabel })
            : t('customers.refill.boxNotice')}
        </p>

        {!canTakePayment ? (
          <p data-needs-bills-create className="mt-3 text-sm text-content-muted">
            {t('tender.needsBillsCreate')}
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || amount <= 0n || !canTakePayment}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
          >
            {submitting ? t('common.loading') : t('customers.refill.submit')}
          </button>
        </div>
      </form>
    </Shell>
  );
}

/**
 * The deposit as the server booked it — the figures the slip prints.
 * Its own component so the render test can reach it: the dialog only gets
 * here after a real POST.
 */
export function RefillDone({
  receipt,
  currencySymbol,
  onClose,
}: {
  receipt: RefillReceiptDto;
  currencySymbol: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const money = (md: string) => formatIqd(fromMillidinars(md), { currencySymbol });

  return (
    <>
      <h2 className="text-lg font-semibold text-content">{t('customers.refill.done')}</h2>
      {/* The number printed on the slip, which Bills finds (owner,
          2026-09-23) — read off the screen when the paper did not print.
          Absent from a server that predates it. */}
      {receipt.receiptNumber ? (
        <p className="mt-1 text-sm font-semibold text-content" data-receipt-number>
          {t('serviceCharge.receiptNumber', { number: receipt.receiptNumber })}
        </p>
      ) : null}
      <p className="mt-1 text-sm text-content-secondary">
        {t('customers.refill.invoice')}: {receipt.invoiceNumber}
      </p>
      <dl className="tabular mt-4 rounded-card bg-raised p-3 text-sm">
        <Row label={t('customers.refill.amount')} value={money(receipt.amountMd)} />
        <Row label={t('customers.refill.bonus')} value={money(receipt.bonusMd)} />
        <Row label={t('customers.refill.credited')} value={money(receipt.totalCreditedMd)} strong />
        <Row label={t('customers.refill.newBalance')} value={money(receipt.balanceAfterMd)} />
      </dl>
      {/* What the slip prints under the balance; a cash deposit never said. */}
      {receipt.method === 'ELECTRONIC' ? (
        <p data-paid-by className="mt-2 text-sm text-content-secondary">
          {t('tender.paidBy', {
            provider: receipt.electronicProviderLabel ?? t('payments.electronic'),
          })}
        </p>
      ) : null}
      <button
        type="button"
        onClick={onClose}
        className="mt-4 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-fg hover:bg-primary-hover"
      >
        {t('common.confirm')}
      </button>
    </>
  );
}

/** Modal shell shared by the money dialogs, so they cannot drift apart. */
export function Shell({
  label,
  onClose,
  children,
}: {
  label: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className="relative max-h-full w-full max-w-md overflow-auto rounded-card border border-subtle bg-ground p-5 shadow-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={label}
          className="absolute top-3 rounded-control p-1 text-content-secondary hover:bg-hover hover:text-content"
          style={{ insetInlineEnd: '0.75rem' }}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
        {children}
      </div>
    </div>
  );
}

export function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between py-0.5">
      <dt className="text-content-secondary">{label}</dt>
      <dd className={strong ? 'font-semibold text-content' : 'text-content-secondary'}>{value}</dd>
    </div>
  );
}
